import { Box, IconButton } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"

import type { CompanyLocationAssociationOutput } from "@/graphql/codegen/graphql"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/utils"

interface AssociatedLocationsTableProps {
  associatedLocationList: CompanyLocationAssociationOutput[]
  onEditClick: (associatedLocation: CompanyLocationAssociationOutput) => void
}

const AssociatedLocationsTable: FunctionComponent<AssociatedLocationsTableProps> = ({
  associatedLocationList,
  onEditClick,
}) => (
  <Box>
    {associatedLocationList.map((associatedLocation) => {
      const { companyLocationAssociationId: currentCompanyLocationAssociationId, companyDetail } = associatedLocation

      const locationAssociations = companyDetail?.company?.locationsAssociations
      const currentAssociation = locationAssociations?.find(
        ({ companyLocationAssociationId }) => companyLocationAssociationId === currentCompanyLocationAssociationId
      )
      const shipViaName = currentAssociation?.shippingVia?.name || ""
      const shippingAddress = formatLocationAddress(currentAssociation?.shipping, AddressFormat.Long)
      const billingAddress = formatLocationAddress(currentAssociation?.billing, AddressFormat.Long)
      const orderAddress = formatLocationAddress(currentAssociation?.order, AddressFormat.Long)
      const officeAddress = formatLocationAddress(currentAssociation?.office, AddressFormat.Long)
      if (!(shippingAddress && billingAddress)) return null

      const columns = [
        ["Shipping", shippingAddress],
        ["Billing", billingAddress],
        ["Order", orderAddress],
        ["Office", officeAddress],
        ["Ship Via", shipViaName],
      ]

      return (
        <Box
          className='flex w-full space-x-8 border border-gray-300 px-6 py-4'
          key={currentCompanyLocationAssociationId}
        >
          {columns.map(([label, address]) => (
            <Box className='w-full' key={label}>
              <label className='mb-2 block text-sm font-normal text-primary'>{label}</label>
              <span className='text-gray-900'>{address}</span>
            </Box>
          ))}
          <Box className='content-end'>
            <IconButton
              color='primary'
              aria-label='edit associated location details'
              onClick={() => onEditClick(associatedLocation)}
            >
              <EditOutlined />
            </IconButton>
          </Box>
        </Box>
      )
    })}
  </Box>
)

export default AssociatedLocationsTable
